/**
 * Extracted HTTP handler logic for the AgentVault plugin.
 *
 * These handlers are shared between:
 * 1. The self-managed HTTP server in channel.ts (legacy/fallback path)
 * 2. OpenClaw's registerHttpRoute() managed routes (new path)
 *
 * Both entry points call the same handler functions, ensuring consistent
 * behavior regardless of how the route is served.
 */

import type { SecureChannel } from "./channel.js";
import type { DeliveryTarget } from "./types.js";

export interface HandlerResult {
  status: number;
  body: Record<string, unknown>;
}

/**
 * Handle POST /send — send a message (text, file, or room).
 *
 * Routing: explicit target fields only — no silent lastInboundRoomId fallback.
 * - hub_address / a2a_address / channel_id → A2A
 * - room_id → room
 * - target: "context" → resolve from lastInboundRoomId (opt-in)
 * - No target fields → owner (the breaking fix)
 */
export async function handleSendRequest(
  parsed: Record<string, unknown>,
  channel: SecureChannel,
): Promise<HandlerResult> {
  const text = parsed.text;
  if (!text || typeof text !== "string") {
    return { status: 400, body: { ok: false, error: "Missing 'text' field" } };
  }

  try {
    // Build DeliveryTarget from request fields
    let target: DeliveryTarget;

    // A2A send: accept hub_address, a2a_address, a2aAddress, or channel_id
    let a2aTarget = (parsed.hub_address ?? parsed.a2a_address ?? parsed.a2aAddress) as string | undefined;

    // Resolve channel_id to hub address if provided
    if (!a2aTarget && parsed.channel_id && typeof parsed.channel_id === "string") {
      const hubAddr = channel.resolveA2AChannelHub(parsed.channel_id as string);
      if (hubAddr) a2aTarget = hubAddr;
    }

    if (a2aTarget && typeof a2aTarget === "string") {
      target = { kind: "a2a", hubAddress: a2aTarget };
    } else if (typeof parsed.room_id === "string") {
      target = { kind: "room", roomId: parsed.room_id };
    } else if (parsed.target === "context") {
      target = { kind: "context" };
    } else if (parsed.file_path && typeof parsed.file_path === "string") {
      // Attachment — goes to owner
      const receipt = await channel.deliver(
        { kind: "owner" },
        { type: "attachment", text: text as string, filePath: parsed.file_path as string },
        { topicId: parsed.topicId as string | undefined },
      );
      return {
        status: receipt.ok ? 200 : 500,
        body: {
          ok: receipt.ok,
          destination: receipt.destination,
          ...(receipt.error ? { error: receipt.error } : {}),
        },
      };
    } else {
      // Default: owner (NOT context — this is the breaking fix)
      target = { kind: "owner" };
    }

    const receipt = await channel.deliver(
      target,
      { type: "text", text: text as string },
      {
        topicId: parsed.topicId as string | undefined,
        priority: parsed.priority as string | undefined,
        metadata: {
          ...(parsed.metadata as Record<string, unknown> | undefined),
          ...(parsed.message_type ? { message_type: parsed.message_type } : {}),
        },
      },
    );

    return {
      status: receipt.ok ? 200 : 500,
      body: {
        ok: receipt.ok,
        destination: receipt.destination,
        ...(receipt.error ? { error: receipt.error } : {}),
      },
    };
  } catch (err) {
    return { status: 500, body: { ok: false, error: String(err) } };
  }
}

/**
 * Handle POST /action — send an action confirmation.
 */
export async function handleActionRequest(
  parsed: Record<string, unknown>,
  channel: SecureChannel,
): Promise<HandlerResult> {
  if (!parsed.action || typeof parsed.action !== "string") {
    return { status: 400, body: { ok: false, error: "Missing 'action' field" } };
  }

  try {
    const confirmation = {
      action: parsed.action as string,
      status: (parsed.status ?? "completed") as "completed" | "failed" | "partial",
      decisionId: parsed.decision_id as string | undefined,
      detail: parsed.detail as string | undefined,
      estimated_cost: parsed.estimated_cost as number | undefined,
    };

    // Build target from room_id field
    const target: DeliveryTarget = (parsed.room_id && typeof parsed.room_id === "string")
      ? { kind: "room", roomId: parsed.room_id }
      : { kind: "owner" };

    const receipt = await channel.deliver(
      target,
      { type: "action_confirmation", confirmation },
    );

    return {
      status: receipt.ok ? 200 : 500,
      body: {
        ok: receipt.ok,
        destination: receipt.destination,
        ...(receipt.error ? { error: receipt.error } : {}),
      },
    };
  } catch (err) {
    return { status: 500, body: { ok: false, error: String(err) } };
  }
}

/**
 * Handle POST /decision — send a decision request to the owner.
 */
export async function handleDecisionRequest(
  parsed: Record<string, unknown>,
  channel: SecureChannel,
): Promise<HandlerResult> {
  const title = parsed.title;
  if (!title || typeof title !== "string") {
    return { status: 400, body: { ok: false, error: "Missing 'title' field" } };
  }

  const options = parsed.options;
  if (!Array.isArray(options) || options.length < 2) {
    return { status: 400, body: { ok: false, error: "'options' must be an array with at least 2 items" } };
  }

  for (const opt of options) {
    if (!opt || typeof opt !== "object" || !opt.option_id || !opt.label) {
      return { status: 400, body: { ok: false, error: "Each option must have 'option_id' and 'label'" } };
    }
  }

  try {
    const decision_id = await channel.sendDecisionRequest({
      title: title as string,
      description: parsed.description as string | undefined,
      options: options as Array<{ option_id: string; label: string; risk_level: "low" | "medium" | "high" | "critical"; is_default?: boolean }>,
      context_refs: parsed.context_refs as Array<{ type: string; uri: string; label: string }> | undefined,
      deadline: parsed.deadline as string | undefined,
      auto_action: parsed.auto_action as { option_id: string; trigger: string; description?: string } | undefined,
    });
    return { status: 200, body: { ok: true, decision_id } };
  } catch (err) {
    return { status: 500, body: { ok: false, error: String(err) } };
  }
}

/**
 * Handle GET /status — return channel health info.
 */
export function handleStatusRequest(channel: SecureChannel): HandlerResult {
  return {
    status: 200,
    body: {
      ok: true,
      state: channel.state,
      deviceId: channel.deviceId ?? undefined,
      sessions: channel.sessionCount,
    },
  };
}

/**
 * Handle GET /targets — return available delivery destinations.
 */
export function handleTargetsRequest(channel: SecureChannel): HandlerResult {
  return {
    status: 200,
    body: {
      ok: true,
      targets: channel.listTargets(),
      context: channel.lastInboundRoomId
        ? { kind: "room", roomId: channel.lastInboundRoomId }
        : { kind: "owner" },
    },
  };
}

/**
 * Handle GET /trust — return the agent's current Trust Gate token state.
 */
export function handleTrustRequest(
  channel: SecureChannel,
): HandlerResult {
  const token = channel.trustToken;
  if (!token) {
    return {
      status: 503,
      body: { ok: false, error: "token_unavailable" },
    };
  }
  return {
    status: 200,
    body: {
      ok: true,
      tier: channel.trustTier,
      composite: null,
      token_expires_at: channel.trustTokenExpiresAt,
    },
  };
}

/**
 * Handle GET /mcp-config — return MCP connection config for this agent.
 *
 * Returns JSON suitable for adding to Claude Code, Cursor, or other MCP host
 * configuration files.
 */
export function handleMcpConfigRequest(
  agentName: string,
  port: number,
  mcpSkillCount: number,
): HandlerResult {
  return {
    status: 200,
    body: {
      mcpServers: {
        [`agentvault-${agentName}`]: {
          url: `http://127.0.0.1:${port}/mcp`,
        },
      },
      _meta: {
        agent: agentName,
        skills_count: mcpSkillCount,
        transport: "streamable-http",
        auth: "none (localhost)",
        instructions: "Add the mcpServers block to your MCP configuration file (e.g., .mcp.json or mcp_config.json)",
      },
    },
  };
}
